import React, { useEffect, useRef, useState } from "react";
import { TABSConstraintsWithoutValues, TABSValueConstriant } from "../types";
import ViewMoreButton from "Components/Common/components/ViewMoreButton";
import Toggle from "Components/DesignSystem/Toggle/Toggle";
import TextField from "Components/DesignSystem/TextField/TextField";
import Dropdown from "Components/DesignSystem/Dropdown/Dropdown";
import Search from "Components/DesignSystem/Search/Search";
import Table from "Components/DesignSystem/Table/Table";
import Spinner from "Components/DesignSystem/Spinner/Spinner";

/**
 * The ABS constraint form — on the design system since Phase 6. All handlers
 * (the abs_properties defaulting effect, handleChange/handleChecked keyed on
 * `e.target.name`, handleValue/handleImage with the verbatim validURL) are the
 * legacy logic; DS Toggle/Dropdown/TextField callbacks synthesize
 * `{ target: { name } }` events. Both switches deliberately keep the shared
 * `attribute-abs-toggle` testid (index-addressed, documented in e2e § 10.2).
 * An invalid image URL now reports as `aria-invalid` via TextField `error`
 * (the legacy mutated the input's className to `is-invalid` by hand). The
 * value rows are a DS Table; a view-more append keeps them mounted with an
 * inline spinner (module loading grammar). The legacy CustomSearchBar's 600 ms
 * default debounce now lives here beside the DS Search.
 */
type MyProps = {
    absDetails: TABSConstraintsWithoutValues,
    setAbsDetails: React.Dispatch<React.SetStateAction<TABSConstraintsWithoutValues|null>>,
    attributeValuesCount: number,
    fetchAbsConstraints: (search?: string, skip?: number, reset?: boolean) => Promise<void>,
    valueConstraints: TABSValueConstriant[]
    setValueConstraints: React.Dispatch<React.SetStateAction<TABSValueConstriant[]>>
    search?: string
    setSearch: React.Dispatch<React.SetStateAction<string|undefined>>
    hasClickedABSToggle: boolean
    setHasClickedABSToggle: React.Dispatch<React.SetStateAction<boolean>>
    loading?: { status?: string, message?: string }
    /**
     * § 288 — a SEARCH fetch is in flight, as opposed to a view-more append. `loading`
     * above cannot tell them apart: one status covers both, and the field must not
     * claim a search when the user clicked "View More".
     */
    searchLoading?: boolean
}

const SEARCH_DEBOUNCE_MS = 600;

export default function EditABS({
    absDetails,
    setAbsDetails,
    attributeValuesCount,
    fetchAbsConstraints,
    valueConstraints,
    setValueConstraints,
    search,
    setSearch,
    hasClickedABSToggle,
    setHasClickedABSToggle,
    loading,
    searchLoading = false
}: MyProps) {
    const VALUE = "value";
    const DISPLAY_VALUE = "displayValue";
    const IMAGE = "image";

    const [absProp, setAbsProp] = useState(
      absDetails && absDetails.abs_properties
    );

    const [searchInput, setSearchInput] = useState(search ?? "");
    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    }, []);
    const handleSearchInput = (value: string) => {
      const trimmed = value.trimStart();
      setSearchInput(trimmed);
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = setTimeout(() => setSearch(trimmed), SEARCH_DEBOUNCE_MS);
    };

    useEffect(() => {
      absDetails && setAbsProp(absDetails.abs_properties);
    }, [absDetails]);

    function getBool(val: string|boolean) {
      return !!JSON.parse(String(val).toLowerCase());
    }

    useEffect(() => {
      if (absDetails.abs_properties) {
        if (absProp.displaySearch === null || undefined) {
          setAbsProp((prevState) => ({
            ...prevState,
            displaySearch: false,
          }));
        }
        if (absProp.valuesPerRow === null || undefined) {
          setAbsProp((prevState) => ({
            ...prevState,
            valuesPerRow: 1,
          }));
        }
        if (absProp.maxHeight === null || undefined) {
          setAbsProp((prevState) => ({
            ...prevState,
            maxHeight: "Not Set",
          }));
        }
        if (absProp.DisplayType === null || undefined) {
          setAbsProp((prevState) => ({
            ...prevState,
            DisplayType: "value",
          }));
        }
      }
      setAbsDetails((prev) => ({
        ...(prev as TABSConstraintsWithoutValues),
        abs_properties: absProp,
      }));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [absProp]);
    const handleChange = (e: { target: { name: string } }, value: string|boolean|number) => {
      let field = e.target.name;
      setAbsProp((prevState) => ({
        ...prevState,
        [field]: value,
      }));
    };
    const handleChecked = (e: { target: { name: string } }, value: string|boolean, isAbsEnable?: boolean) => {
      let field = e.target.name;
      if(!hasClickedABSToggle) setHasClickedABSToggle(true)
      setAbsDetails((prevState) => {
				const nextState = {
						...(prevState as TABSConstraintsWithoutValues),
						[field]: value,
				}

				if(isAbsEnable) nextState['abs_enable_changed'] = true

				return nextState
			});
    };

    useEffect(() => {
      const isAnyAbsShowTrue = valueConstraints.some(
        (singleValue) => singleValue.abs_show
      );

      if (absDetails.abs_enabled) {
        if (!isAnyAbsShowTrue) {
            setValueConstraints((prev) => {
              const updatedValueConstraints = [...prev].map(
                (singleValue) => {
                  return { ...singleValue, abs_show: true };
                }
            );
            return updatedValueConstraints
          });
        }
      } else {
        if (isAnyAbsShowTrue) {
            setValueConstraints((prev) => {
              const updatedValueConstraints = [...prev].map(
                (singleValue) => {
                  return { ...singleValue, abs_show: false };
                }
            );
            return updatedValueConstraints
          });
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [absDetails?.abs_enabled]);

    function handleValue(e: { target: { name: string } }, value: boolean|string, index: number) {
      let arr = [...valueConstraints];
      let obj = { ...arr[index] };
      obj = { ...obj, [e.target.name]: value, changed: true };
      arr.splice(index, 1, obj);
      setValueConstraints(arr)
    }

    function handleImage(e: { target: { name: string } }, value: string, index: number, isValidUrl: boolean) {
      let arr = [...valueConstraints];
      let obj = { ...arr[index] };
      obj = { ...obj, [e.target.name]: value, isValidUrl: isValidUrl, changed: true };
      arr.splice(index, 1, obj);
      setValueConstraints(arr)
    }

    const validURL = (str: string) => {
      if (str == null || undefined || "" || str.length === 0) {
        return true;
      }
      let url;
      try {
        url = new URL(str);
      } catch (_) {
        return false;
      }

      if (url.protocol === "http:" || url.protocol === "https:") {
        let pattern = new RegExp(
          "^(https?:\\/\\/)?" + // protocol
            "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + // domain name
            "((\\d{1,3}\\.){3}\\d{1,3}))" + // OR ip (v4) address
            "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // port and path
            "(\\?[;&a-z\\d%_.~+=-]*)?" + // query string
            "(\\#[-a-z\\d_]*)?$",
          "i"
        ); // fragment locator
        return !!pattern.test(str);
      }
    };

    return (
      <>
        {absProp && (
          <div className="tw-space-y-4">
            <Toggle
              label="Activate this attribute for ABS"
              data-testid="attribute-abs-toggle"
              checked={getBool(absDetails.abs_enabled)}
              onChange={() =>
                handleChecked({ target: { name: "abs_enabled" } }, !absDetails.abs_enabled, true)
              }
            />
            <Toggle
              label="Display Search?"
              data-testid="attribute-abs-toggle"
              disabled={!absDetails.abs_enabled}
              checked={getBool(absProp?.displaySearch ?? false)}
              onChange={() =>
                handleChange(
                  { target: { name: "displaySearch" } },
                  !getBool(absProp?.displaySearch ?? false)
                )
              }
            />

            <TextField
              label="Display Name"
              size="sm"
              type="text"
              data-testid="componets-editabs-input-1"
              id="floatingInput"
              name="displayName"
              disabled={!absDetails.abs_enabled}
              value={absProp.displayName ?? ""}
              placeholder="name"
              onChange={(value) =>
                handleChange({ target: { name: "displayName" } }, value)
              }
            />

            <div className="tw-grid tw-grid-cols-12 tw-gap-4">
              <div className="tw-col-span-3">
                <Dropdown
                  label="Display Type"
                  size="sm"
                  data-testid="componets-editabs-select-2"
                  disabled={!absDetails.abs_enabled}
                  options={[
                    { value: VALUE, label: "No" },
                    { value: DISPLAY_VALUE, label: "Yes, Text" },
                    { value: IMAGE, label: "Yes, Images" },
                  ]}
                  value={absProp.DisplayType}
                  onChange={(value) =>
                    handleChange({ target: { name: "DisplayType" } }, value)
                  }
                />
              </div>
              <div className="tw-col-span-3">
                <Dropdown
                  label="Values Per Row"
                  size="sm"
                  data-testid="componets-editabs-select-3"
                  disabled={!absDetails.abs_enabled}
                  options={[
                    { value: 1, label: "One Value" },
                    { value: 2, label: "Two Values" },
                    { value: 3, label: "Three Values" },
                    { value: 4, label: "Four Values" },
                    { value: 5, label: "Five Values" },
                  ]}
                  value={absProp.valuesPerRow}
                  onChange={(value) =>
                    handleChange({ target: { name: "valuesPerRow" } }, parseInt(String(value)))
                  }
                />
              </div>
              <div className="tw-col-span-3">
                <Dropdown
                  label="Max Height"
                  size="sm"
                  data-testid="componets-editabs-select-4"
                  disabled={!absDetails.abs_enabled}
                  options={[
                    { value: "Not Set", label: "Not Set" },
                    { value: "Small", label: "Small" },
                    { value: "Medium", label: "Medium" },
                    { value: "Large", label: "Large" },
                  ]}
                  value={absProp.maxHeight}
                  onChange={(value) =>
                    handleChange({ target: { name: "maxHeight" } }, value)
                  }
                />
              </div>
            </div>

            <Search
              size="sm"
              className="tw-w-60"
              placeholder="Search Attribute Values"
              ariaLabel="Search attribute values"
              data-testid="attribute-abs-search-input"
              value={searchInput}
              onChange={handleSearchInput}
              /* § 288 — the search-only flag (a view-more append must not spin the
                 field), plus the 600ms debounce in front of it. */
              loading={searchLoading || searchInput !== (search ?? "")}
              loadingLabel="Searching values"
            />

            {valueConstraints &&
              valueConstraints.length > 0 && (
                <>
                  <Table
                    data={valueConstraints}
                    rowKey={(row: TABSValueConstriant) => row.value}
                    data-testid="attribute-abs-values-table"
                    columns={[
                      {
                        id: "abs_show",
                        header: "Display",
                        accessor: "abs_show",
                        render: (_value: unknown, singleValue: TABSValueConstriant, index: number) => (
                          <Toggle
                            label=""
                            aria-label={`Show ${singleValue.value}`}
                            data-testid="componets-editabs-input-5"
                            disabled={!absDetails.abs_enabled}
                            checked={getBool(singleValue.abs_show)}
                            onChange={() =>
                              handleValue(
                                { target: { name: "abs_show" } },
                                !getBool(singleValue.abs_show),
                                index
                              )
                            }
                          />
                        ),
                      },
                      { id: "value", header: "Value", accessor: "value" },
                      {
                        id: "abs_display_value",
                        header: "ABS Display Value",
                        accessor: "abs_display_value",
                        render: (_value: unknown, singleValue: TABSValueConstriant, index: number) => (
                          <TextField
                            label=""
                            size="sm"
                            type="text"
                            data-testid="componets-editabs-input-6"
                            name="abs_display_value"
                            disabled={!absDetails.abs_enabled}
                            value={singleValue.abs_display_value ?? ""}
                            placeholder="ABS Display value"
                            onChange={(value) =>
                              handleValue({ target: { name: "abs_display_value" } }, value, index)
                            }
                          />
                        ),
                      },
                      {
                        id: "abs_image_url",
                        header: "ABS Image Url",
                        accessor: "abs_image_url",
                        render: (_value: unknown, singleValue: TABSValueConstriant, index: number) => (
                          <TextField
                            label=""
                            size="sm"
                            type="text"
                            data-testid="componets-editabs-input-7"
                            name="abs_image_url"
                            disabled={!absDetails.abs_enabled}
                            value={singleValue.abs_image_url ?? ""}
                            error={singleValue.isValidUrl === false}
                            placeholder="ABS Image Url starts with https:// or http://"
                            onChange={(value) => {
                              if (validURL(value) || value === "") {
                                handleImage({ target: { name: "abs_image_url" } }, value, index, true);
                              } else {
                                handleImage({ target: { name: "abs_image_url" } }, value, index, false);
                              }
                            }}
                          />
                        ),
                      },
                      {
                        id: "abs_alt_text",
                        header: "ABS Alt text",
                        accessor: "abs_alt_text",
                        render: (_value: unknown, singleValue: TABSValueConstriant, index: number) => (
                          <TextField
                            label=""
                            size="sm"
                            type="text"
                            data-testid="componets-editabs-input-8"
                            name="abs_alt_text"
                            disabled={!absDetails.abs_enabled}
                            value={singleValue.abs_alt_text ?? ""}
                            placeholder="ABS Alt text"
                            onChange={(value) =>
                              handleValue({ target: { name: "abs_alt_text" } }, value, index)
                            }
                          />
                        ),
                      },
                    ]}
                  />

                  {loading?.status === "Loading" ? (
                    <span
                      className="tw-inline-flex tw-items-center tw-px-2"
                      data-testid="attribute-abs-loading-more"
                    >
                      <Spinner size="sm" label="Loading more values" />
                    </span>
                  ) : (
                    <ViewMoreButton
                      condition = {valueConstraints.length < attributeValuesCount}
                      state = {valueConstraints.length >= attributeValuesCount}
                      handleClick = {() => fetchAbsConstraints(search, valueConstraints.length)}
                    />
                  )}
                </>
              )}
          </div>
        )}
      </>
    );
  }
